import { useState } from 'react';
import {
  useDeleteSftpCredential,
  useSftpCredentials,
  useUpsertSftpCredential,
} from '../../api/credentials.js';

/**
 * SFTP credentials admin (CSAGE-268). Create/list/delete tenant-scoped SSH
 * credentials that an sftp subscription references by name (secretRef). The
 * private key is write-only: entered here, stored encrypted server-side, and
 * never displayed back. Scope-filtered server-side to the caller's tenant.
 */
export function SftpCredentialsManager(): JSX.Element {
  const list = useSftpCredentials();
  const upsert = useUpsertSftpCredential();
  const del = useDeleteSftpCredential();

  const [name, setName] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const nameValid = /^[A-Za-z0-9._-]+$/.test(name.trim());
  const canSubmit = nameValid && privateKey.trim().length > 0;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    await upsert.mutateAsync({
      name: name.trim(),
      privateKey,
      ...(passphrase.trim() ? { passphrase } : {}),
    });
    setName('');
    setPrivateKey('');
    setPassphrase('');
  }

  return (
    <div className="card-body stack-lg" style={{ maxWidth: 760 }}>
      <div className="section-head">
        <div className="section-head-title">
          <h2>SFTP credentials</h2>
          <p>
            SSH keys for SFTP delivery. Referenced by name from a subscription's SFTP channel. Keys
            are stored encrypted and never shown again after saving.
          </p>
        </div>
      </div>

      {list.isLoading && <div className="text-dim">Loading…</div>}
      {list.isError && <div className="alert alert--danger">Failed to load credentials.</div>}
      {list.data && list.data.length > 0 && (
        <div className="card" style={{ background: 'var(--color-surface-2)' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Passphrase</th>
                <th>Added</th>
                <th style={{ width: 100, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                  </td>
                  <td>
                    {c.hasPassphrase ? (
                      <span className="badge badge--neutral">yes</span>
                    ) : (
                      <span className="text-mute">—</span>
                    )}
                  </td>
                  <td className="text-sm text-dim">{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={del.isPending}
                      onClick={() => setPendingDelete(c.name)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data && list.data.length === 0 && (
        <div className="text-mute text-sm">No SFTP credentials yet.</div>
      )}

      <form onSubmit={submit} className="card" style={{ padding: 'var(--space-4)' }}>
        <h3 style={{ marginTop: 0 }}>Add / replace a credential</h3>
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="payouts"
          />
          {name.length > 0 && !nameValid && (
            <div className="field-help">Only letters, numbers, and . _ - are allowed.</div>
          )}
        </label>
        <label className="field">
          <span className="field-label">SSH private key (PEM)</span>
          <textarea
            className="textarea input--mono"
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n…'}
            rows={6}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="field-help">Stored encrypted; never displayed again after saving.</div>
        </label>
        <label className="field">
          <span className="field-label">Passphrase (optional)</span>
          <input
            className="input"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            autoComplete="off"
          />
        </label>
        {upsert.isError && (
          <div className="alert alert--danger">
            Save failed
            {(upsert.error as Error)?.message ? `: ${(upsert.error as Error).message}` : ''}.
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={!canSubmit || upsert.isPending}
          >
            {upsert.isPending ? 'Saving…' : 'Save credential'}
          </button>
        </div>
      </form>

      {pendingDelete && (
        <div className="modal-backdrop" onClick={() => setPendingDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <header className="modal-header">
              <h2>Delete credential</h2>
            </header>
            <div className="modal-body">
              <p>
                Delete SFTP credential <strong>{pendingDelete}</strong>? Any subscription that
                references it will fail to deliver until re-pointed.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(pendingDelete, { onSuccess: () => setPendingDelete(null) })
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
